import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Keeps the free gifts of the offer in step with the sensors in the cart: a gift goes in
 * once the cart holds enough sensors and comes out when it no longer does. One of each.
 *
 * What the cart should hold is worked out in Liquid (sections/ell-cart-gifts.liquid) and
 * asked for after each change of the cart. The gifts are made free by a discount in the
 * store, not here. If a gift this script added comes back with a price, it is taken out
 * again and nothing more is added on this visit, so no customer pays for a gift.
 */

const SECTION = 'ell-cart-gifts';
const STOPPED = 'ell-cart-gifts-stopped';

let busy = false;
let again = false;

/** @returns {boolean} */
function stopped() {
  try {
    return sessionStorage.getItem(STOPPED) === '1';
  } catch {
    return false;
  }
}

function stop() {
  try {
    sessionStorage.setItem(STOPPED, '1');
  } catch {
    // Storage is off. The next change of the cart tries once more and stops the same way.
  }
}

/**
 * Reads what the cart should hold of the gifts.
 * @returns {Promise<{ sensors: number, gifts: { variant: number, want: number, have: number }[] } | null>}
 */
async function wanted() {
  const response = await fetch(`${Theme.routes.cart_url}?section_id=${SECTION}`, { credentials: 'same-origin' });
  if (!response.ok) return null;

  const page = new DOMParser().parseFromString(await response.text(), 'text/html');
  const data = page.querySelector('[data-ell-cart-gifts]')?.textContent;
  return data ? JSON.parse(data) : null;
}

/**
 * Sets the quantity of some variants, and has the theme redraw its carts.
 * @param {Record<string, number>} updates - Variant id to quantity.
 * @returns {Promise<any | null>} The cart, as Shopify's Ajax API returns it.
 */
async function update(updates) {
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((cart) => (cart instanceof HTMLElement ? cart.dataset.sectionId : ''))
    .filter(Boolean);
  const { promise, resolve, reject } = CartLinesUpdateEvent.createPromise();

  // the theme's carts and its cart icon redraw from this
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: Object.entries(updates).map(([id, quantity]) => ({ merchandiseId: id, quantity })),
      promise,
    })
  );

  try {
    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sections.join(',') }),
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart?.message || 'The gifts could not be updated');

    resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: SECTION, sections: cart.sections, itemCount: cart.item_count, didError: false },
    });
    return cart;
  } catch (error) {
    reject(error);
    return null;
  }
}

async function reconcile() {
  if (busy) {
    again = true;
    return;
  }
  busy = true;

  try {
    const state = await wanted();
    if (!state) return;

    /** @type {Record<string, number>} */
    const updates = {};
    for (const gift of state.gifts) {
      // a gift that is wanted is not added once this visit has seen one charged
      const want = gift.want && stopped() && !gift.have ? 0 : gift.want;
      if (gift.have !== want) updates[String(gift.variant)] = want;
    }
    if (!Object.keys(updates).length) return;

    const cart = await update(updates);
    if (!cart) return;

    // No customer pays for a gift: one that came back with a price goes out again.
    /** @type {Record<string, number>} */
    const charged = {};
    for (const item of cart.items) {
      if (updates[String(item.variant_id)] === 1 && item.final_line_price > 0) charged[String(item.variant_id)] = 0;
    }
    if (Object.keys(charged).length) {
      stop();
      await update(charged);
    }
  } catch (error) {
    console.warn('[ell-cart-gifts]', error);
  } finally {
    busy = false;
    if (again) {
      again = false;
      reconcile();
    }
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const { promise } = /** @type {CartLinesUpdateEvent} */ (event);

  Promise.resolve(promise)
    .then((result) => {
      // this script's own change of the cart needs no second look
      if (result?.detail?.source === SECTION) return;
      reconcile();
    })
    .catch(() => {
      // The change failed, so the cart is as it was.
    });
});

// A cart from an earlier visit, or one changed outside the theme's own buttons.
if (document.cookie.includes('cart=')) reconcile();
